import React, { useEffect, useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { sendGA4, sendGAEvent } from '../../../utils/common'
import ElementInView from '../../helpers/ElementInView/ElementInView'
import s from './SearchBar.scss'

const SearchBar = (props) => {
  const {
    placeHolderPrefix,
    placeHolderTexts,
    affiliateDetails = {},
    tag,
    ga,
    useGA4 = false,
    className,
  } = props
  const [placeHolderTextValue, setPlaceHolderTextValue] = useState('')
  const [placeHolderTextIndex, setPlaceHolderTextIndex] = useState(0)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isViewed, setIsViewed] = useState(false)
  const [isSearchInFocus, setIsSearchInFocus] = useState(false)

  const inputRef = useRef()

  if (!affiliateDetails.baseURL) {
    return null
  }

  const containerClasses = classNames({
    [s.container]: true,
    [className]: !!className,
  })

  const placeHolderClasses = classNames({
    [s.placeholder]: true,
    [s.hide]: searchKeyword || isSearchInFocus,
  })

  const searchClearClasses = classNames({
    [s.search_clear]: true,
    [s.hide]: !searchKeyword,
  })

  let placeHolderTypingTimer

  useEffect(() => {
    if (isViewed) {
      if (placeHolderTypingTimer) {
        clearInterval(placeHolderTypingTimer)
      }
      setTimeout(() => {
        setPlaceHolderTextValue('')
        placeHolderTypingTimer = setInterval(() => {
          setPlaceHolderTextValue((value) => {
            const placeHolderText = placeHolderTexts[placeHolderTextIndex]
            if (value.length === placeHolderText.length - 1) {
              clearInterval(placeHolderTypingTimer)
              setPlaceHolderTextIndex((indexValue) =>
                indexValue < placeHolderTexts.length - 1 ? indexValue + 1 : 0
              )
            }
            return `${value}${placeHolderText[value.length]}`
          })
        }, 200)
      }, 500)
    }
  }, [isViewed, placeHolderTextIndex])

  const onSearch = useCallback((event) => {
    if (event.key === 'Enter') {
      const { baseURL, searchParam, intermediatePage } = affiliateDetails
      const searchTerm = event.target.value
      const searchURL = `${baseURL}?${searchParam}=${searchTerm}&tag=${tag}`
      const finalURL = `${intermediatePage}${encodeURIComponent(searchURL)}`
      if (useGA4) {
        sendGA4('user_click', {
          type: 'affiliate_search',
          category: '',
          label: searchTerm,
        })
      } else {
        sendGAEvent({
          category: ga.category,
          action: ga.action,
          label: searchTerm,
        })
      }
      window.open(finalURL)
    }
  })

  const onChange = useCallback((event) => {
    setSearchKeyword(event.target.value)
  })

  const onSearchFocus = useCallback(() => {
    setIsSearchInFocus(true)
  })

  const onSearchBlur = useCallback(() => {
    setIsSearchInFocus(false)
  })

  const clearSearch = useCallback(() => {
    setSearchKeyword('')
  })

  const focusSearch = useCallback(() => {
    inputRef.current.focus()
  })

  return (
    <ElementInView
      inViewCallback={() => {
        setIsViewed(true)
      }}
      viewMargin="-50px 0px -50px 0px"
      threshold={[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]}
    >
      <div className={containerClasses}>
        <input
          className={s.search_input}
          type="text"
          onKeyUp={onSearch}
          onChange={onChange}
          value={searchKeyword}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          ref={inputRef}
        />
        <span className={s.search_icon}>
          <span className={s.search_circle} />
          <span className={s.search_rectangle} />
        </span>
        <span className={placeHolderClasses} onClick={focusSearch}>
          {placeHolderPrefix} <strong>{placeHolderTextValue}</strong>
        </span>
        <span className={searchClearClasses} onClick={clearSearch} />
      </div>
    </ElementInView>
  )
}

SearchBar.propTypes = {
  placeHolderPrefix: PropTypes.string,
  placeHolderTexts: PropTypes.arrayOf(PropTypes.string),
  tag: PropTypes.string.isRequired,
  affiliateDetails: PropTypes.shape({
    baseURL: PropTypes.string.isRequired,
    searchParam: PropTypes.string.isRequired,
  }).isRequired,
  ga4: PropTypes.shape({}),
  ga: PropTypes.shape({
    category: PropTypes.string,
    action: PropTypes.string,
  }),
  className: PropTypes.string,
  useGA4: PropTypes.bool,
}

SearchBar.defaultProps = {
  placeHolderPrefix: 'Search for',
  placeHolderTexts: ['Products'],
  className: '',
  ga: {},
  ga4: {},
  useGA4: false,
}

export default SearchBar
